import { useEffect, useRef, useState } from 'react';
import { Activity, RefreshCw, Radio, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DailySnapshotsPanel } from './DailySnapshotsPanel';
import ReportDateRangePicker from '../../components/ReportDateRangePicker';
import CtBadge from '../../components/CtBadge';
import PlayerNameLink from '../../components/PlayerNameLink';
import TreasuryReportCard from './TreasuryReportCard';
import LoadsByChannelCard from '../../components/LoadsByChannelCard';
import CreditReconciliationCard from '../../components/admin/CreditReconciliationCard';
import TopCreditConsumersCard from '../../components/admin/TopCreditConsumersCard';
import ParityAlertsCard from '../../components/admin/ParityAlertsCard';
import OutstandingLoansCard from '../../components/admin/OutstandingLoansCard';
import SquareReceiptsQueueCard from '../../components/admin/SquareReceiptsQueueCard';
import AfterHoursAutoCreditCard from '../../components/admin/AfterHoursAutoCreditCard';
import ReceiptsHistoryPanel from '../../components/ReceiptsHistoryPanel';
import TonkCrashLogCard from '../../components/admin/TonkCrashLogCard';
import CashierPerformanceTile from '../../components/admin/CashierPerformanceTile';
import BlackjackReportCard from '../../components/admin/BlackjackReportCard';
import ThreeCardPokerReportCard from '../../components/admin/ThreeCardPokerReportCard';
import CrapsReportCard from '../../components/admin/CrapsReportCard';
import RouletteReportCard from '../../components/admin/RouletteReportCard';
import StreetDiceReportCard from '../../components/admin/StreetDiceReportCard';
import LotteryDailyWinnersCard from '../../components/admin/LotteryDailyWinnersCard';
import CollapsibleSection from '../../components/admin/CollapsibleSection';

const REFRESH_INTERVAL_MS = 15000; // 15s — true real-time-ish

export const ReportsAdminTab = ({
    financialReport,
    allBetsReport,
    lotteryTickets,
    promoReport,
    reportsLoading,
    fetchReports,
}) => {
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [dateRange, setDateRange] = useState({ start: null, end: null, startIso: null, endIso: null });
    const intervalRef = useRef(null);

    // Auto-refresh tick (also re-issues with current date range)
    useEffect(() => {
        if (!autoRefresh || !financialReport) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }
        intervalRef.current = setInterval(async () => {
            await fetchReports({ silent: true, startDate: dateRange.startIso, endDate: dateRange.endIso });
            setLastRefreshed(new Date());
        }, REFRESH_INTERVAL_MS);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoRefresh, financialReport, fetchReports, dateRange.startIso, dateRange.endIso]);

    const handleManualRefresh = async () => {
        await fetchReports({ startDate: dateRange.startIso, endDate: dateRange.endIso });
        setLastRefreshed(new Date());
    };

    const handleDateChange = async (r) => {
        setDateRange(r);
        await fetchReports({ startDate: r.startIso, endDate: r.endIso });
        setLastRefreshed(new Date());
    };

    const lastRefreshedText = lastRefreshed
        ? lastRefreshed.toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit', second: '2-digit' }) + ' CT'
        : '—';

    const customWindow = financialReport?.custom_window;
    const customData = financialReport?.custom;
    const customPrev = financialReport?.custom_previous;

    const pctDelta = (curr, prev) => {
        if (prev == null || prev === 0) return null;
        return ((curr - prev) / Math.abs(prev)) * 100;
    };

    return (
        <div className="space-y-6">
            {/* Daily parity alerts — small but loud red/yellow strip for
                cash-drawer / casino-drain / promo-budget anomalies. Shown only
                when there's something to act on. */}
            <ParityAlertsCard />

            {/* Daily Credit Reconciliation — answers "where did all my credits go
                today?" with categorized buckets and click-to-drilldown. Rendered
                FIRST so it's the very first thing the admin sees. */}
            <CreditReconciliationCard />

            {/* Square receipts — player uploads, OCR pre-fills, 1-click credit. */}
            <CollapsibleSection title="Square receipts" subtitle="Player uploads to credit · OCR pre-fill" testid="reports-collapse-square-receipts">
                <SquareReceiptsQueueCard />
            </CollapsibleSection>

            {/* After-hours auto-credit — toggle + morning audit queue */}
            <CollapsibleSection title="24/7 auto-credit audit" subtitle="After-hours toggle + morning audit queue" testid="reports-collapse-auto-credit">
                <AfterHoursAutoCreditCard />
            </CollapsibleSection>

            {/* Full historical receipt audit — searchable, filterable, click any
                row to open a viewer with the original screenshot + OCR details */}
            <CollapsibleSection title="All receipts (history)" subtitle="Searchable audit of every receipt" testid="reports-collapse-receipts-history">
                <ReceiptsHistoryPanel />
            </CollapsibleSection>

            {/* Tonk client-crash log — self-hides when nothing has crashed in
                the last 7 days. Populated by the frontend `TonkPageBoundary`
                POSTing to /api/errors/tonk-crash on every render crash. */}
            <TonkCrashLogCard />

            {/* Cashier productivity rollup — added iter 158 after every self-
                signup started auto-attributing to Winnie. Shows loads/redeems/
                net/player-count per cashier for today + 7d. */}
            <CollapsibleSection title="Cashier performance" subtitle="Loads / redeems / net per cashier" testid="reports-collapse-cashier-perf">
                <CashierPerformanceTile />
            </CollapsibleSection>

            {/* Top Credit Consumers — sorted leaderboard with risk-ratio flag for
                possible abusers (lifetime playbacks > 50% of loads). */}
            <CollapsibleSection title="Top credit consumers" subtitle="Leaderboard + risk-ratio flags" testid="reports-collapse-top-consumers">
                <TopCreditConsumersCard />
            </CollapsibleSection>

            {/* Outstanding loans — running ledger of who still owes the house. */}
            <CollapsibleSection title="Outstanding loans" subtitle="Who still owes the house" testid="reports-collapse-loans">
                <OutstandingLoansCard />
            </CollapsibleSection>

            {/* End-of-Day Treasury — always shown, independent of the rest of the
                financial report which can be lazy-loaded. */}
            <TreasuryReportCard />

            {/* "Loaded In by Channel" — splits the daily/weekly/monthly load total
                across every channel (Cashier counter, Admin manual,
                Kiosk, etc) so the admin can instantly spot if one channel
                suddenly drops or jumps. */}
            <CollapsibleSection title="Loaded in by channel" subtitle="Daily / weekly / monthly load split" testid="reports-collapse-loads-channel">
                <LoadsByChannelCard />
            </CollapsibleSection>

            {/* Sportsbook report, manual grading and limits now live in their own
                dedicated "Sports" section (top nav) — kept out of Reports so all
                sportsbook admin is in one place. */}

            {/* Lottery winners by day — which tickets won each draw. Always
                visible (self-loading), like the sportsbook report. */}
            <CollapsibleSection title="Lottery winners by day" subtitle="Which tickets won each draw · last 14 draws" defaultOpen testid="reports-collapse-lottery-winners">
                <LotteryDailyWinnersCard />
            </CollapsibleSection>

            {/* House-table financials — mirrored under Reports so the admin
                can see all four in-house tables in one place instead of
                jumping into the Slots tab. Wallet-native so wagers appear in
                the same transactions ledger as everything else. */}
            <CollapsibleSection title="Game table financials" subtitle="Blackjack · 3-Card Poker · Craps · Roulette · Street Dice" testid="reports-collapse-game-tables">
                <div className="space-y-6">
                    <BlackjackReportCard />
                    <ThreeCardPokerReportCard />
                    <CrapsReportCard />
                    <RouletteReportCard />
                    <StreetDiceReportCard />
                </div>
            </CollapsibleSection>

            {!financialReport && !reportsLoading && (
                <div className="text-center py-12">
                    <Button onClick={handleManualRefresh} className="bg-[#FFD700] text-black" data-testid="load-reports-btn">
                        <Activity className="w-4 h-4 mr-2" />Load Reports
                    </Button>
                </div>
            )}
            {reportsLoading && !financialReport && (
                <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 text-[#FFD700] animate-spin" />
                </div>
            )}
            {financialReport && (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="text-lg font-bold text-white">Financial Report</h3>
                            <CtBadge testId="reports-ct-badge" />
                            {autoRefresh && (
                                <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/40 text-[#39FF14] text-[10px] font-bold uppercase"
                                    data-testid="reports-live-badge">
                                    <Radio className="w-2.5 h-2.5 animate-pulse" />
                                    Live
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <ReportDateRangePicker
                                value={dateRange}
                                onChange={handleDateChange}
                                testId="reports-date-picker"
                            />
                            <span className="text-[10px] text-[#A0AAB2] hidden sm:inline" data-testid="reports-last-refreshed">
                                Last: {lastRefreshedText}
                            </span>
                            <label className="flex items-center gap-2 text-xs text-[#A0AAB2] cursor-pointer">
                                <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} data-testid="reports-auto-refresh-toggle" />
                                Auto-refresh
                            </label>
                            <Button size="sm" variant="outline" onClick={handleManualRefresh}
                                className="border-[#FFD700]/30 text-[#FFD700]"
                                data-testid="reports-manual-refresh-btn">
                                <RefreshCw className={`w-3 h-3 mr-1 ${reportsLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                        </div>
                    </div>

                    {/* Custom date range result */}
                    {customWindow && customData && (
                        <div className="card-dark p-5 border-2 border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/5 to-transparent" data-testid="custom-window-card">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <div>
                                    <h4 className="text-base font-black text-[#FFD700] uppercase tracking-wider">
                                        {customWindow.days === 1
                                            ? `Report for ${customWindow.start_date}`
                                            : `Report · ${customWindow.start_date} → ${customWindow.end_date} (${customWindow.days} days)`}
                                    </h4>
                                    {customPrev && (
                                        <p className="text-[10px] text-[#A0AAB2]">
                                            vs prior {customWindow.days === 1 ? 'day' : `${customWindow.days} days`} ({customWindow.previous_start_date} → {customWindow.previous_end_date})
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { key: 'loaded',  label: 'Loaded In',    val: customData.deposits?.total || 0,    prev: customPrev?.deposits?.total || 0,    color: '#39FF14', sign: '+' },
                                    { key: 'out',     label: 'Redeemed Out', val: customData.withdrawals?.total || 0, prev: customPrev?.withdrawals?.total || 0, color: '#FF3B30', sign: '-' },
                                    { key: 'wagered', label: 'Total Wagered', val: customData.combined?.total_wagered || 0, prev: customPrev?.combined?.total_wagered || 0, color: '#A0AAB2', sign: '' },
                                    { key: 'profit',  label: 'Net cash flow after free play (not profit)',  val: customData.combined?.cash_profit ?? customData.combined?.profit ?? 0, prev: customPrev?.combined?.cash_profit ?? customPrev?.combined?.profit ?? 0, color: null, sign: '' },
                                ].map((s) => {
                                    const delta = customPrev ? pctDelta(s.val, s.prev) : null;
                                    const isProfit = s.key === 'profit';
                                    const dispColor = isProfit ? (s.val >= 0 ? '#39FF14' : '#FF3B30') : s.color;
                                    return (
                                        <div key={s.key} className="rounded-lg bg-black/30 border border-white/10 p-3" data-testid={`custom-stat-${s.key}`}>
                                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">{s.label}</div>
                                            <div className="font-mono text-2xl font-black mt-0.5" style={{ color: dispColor }}>
                                                {s.sign}${Math.abs(s.val).toFixed(2)}
                                            </div>
                                            {delta !== null && (
                                                <div className={`flex items-center gap-1 text-[10px] font-bold mt-1 ${delta >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                                    {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                                                    <span className="text-[#A0AAB2] font-normal ml-1">vs prior</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-white/10 text-xs">
                                <div>
                                    <span className="text-[10px] text-[#3B82F6] uppercase font-bold">Sports</span>
                                    <p className="text-white font-mono">${(customData.sports?.wagered || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.sports?.count || 0} bets</p>
                                </div>
                                <div>
                                    <span className="text-[10px] text-[#F59E0B] uppercase font-bold">Lottery</span>
                                    <p className="text-white font-mono">${(customData.lottery?.wagered || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.lottery?.count || 0} tickets</p>
                                </div>
                                <div>
                                    <span className="text-[10px] text-[#FFD700] uppercase font-bold">Blackjack</span>
                                    <p className="text-white font-mono">${(customData.blackjack?.wagered || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.blackjack?.count || 0} hands</p>
                                </div>
                                <div data-testid="custom-3cp">
                                    <span className="text-[10px] text-[#00E1FF] uppercase font-bold">3-Card Poker</span>
                                    <p className="text-white font-mono">${(customData.three_card_poker?.wagered || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.three_card_poker?.rounds || 0} rounds</p>
                                </div>
                                <div data-testid="custom-craps">
                                    <span className="text-[10px] text-[#39FF14] uppercase font-bold">Craps</span>
                                    <p className="text-white font-mono">${(customData.craps?.wagered || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.craps?.rolls || 0} rolls</p>
                                </div>
                                <div>
                                    <span className="text-[10px] text-[#39FF14] uppercase font-bold">Slots</span>
                                    <p className="text-white font-mono">${(customData.slots?.wagered || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.slots?.count || 0} spins</p>
                                </div>
                                <div>
                                    <span className="text-[10px] text-[#A855F7] uppercase font-bold">Free Play</span>
                                    <p className="text-white font-mono">${(customData.free_play?.total || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.free_play?.count || 0} times</p>
                                </div>
                                <div data-testid="custom-played-back">
                                    <span className="text-[10px] text-[#00E0FF] uppercase font-bold">Played Back</span>
                                    <p className="text-white font-mono">${(customData.played_back?.total || 0).toFixed(0)} <span className="text-[#A0AAB2]">·</span> {customData.played_back?.count || 0} events</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {['today', 'week', 'month', 'year'].map(period => {
                            const p = financialReport[period] || {};
                            const combined = p.combined || {};
                            const sports = p.sports || {};
                            const lottery = p.lottery || {};
                            const blackjack = p.blackjack || {};
                            const tcp = p.three_card_poker || {};
                            const craps = p.craps || {};
                            const slots = p.slots || {};
                            const casino = p.casino || {};
                            const pending = p.pending_cashouts || { total: 0, count: 0 };
                            return (
                                <div key={period} className="card-dark p-4">
                                    <h4 className="text-sm font-bold text-[#FFD700] uppercase mb-3">{period === 'today' ? 'Today' : period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : 'This Year'}</h4>
                                    <div className="space-y-1.5 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-[#A0AAB2]">Loaded In</span>
                                            <span className="text-[#39FF14] font-mono">+{(p.deposits?.total || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[#A0AAB2]">Redeemed Out</span>
                                            <span className="text-[#FF3B30] font-mono">-{(p.withdrawals?.total || 0).toFixed(2)}</span>
                                        </div>
                                        {pending.count > 0 && (
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] flex items-center gap-1">
                                                    Pending
                                                    <span className="text-[9px] px-1 rounded bg-[#FFD700]/20 text-[#FFD700] font-bold">{pending.count}</span>
                                                </span>
                                                <span className="text-[#FFD700] font-mono">~{(pending.total || 0).toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between">
                                            <span className="text-[#A0AAB2]">Free Play Given</span>
                                            <span className="text-[#A855F7] font-mono">-{(promoReport?.[period]?.total || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between" data-testid={`played-back-${period}`}>
                                            <span className="text-[#A0AAB2] flex items-center gap-1" title="Cashouts the player chose to play back into their wallet instead of withdrawing">
                                                Played Back
                                                {(p.played_back?.count || 0) > 0 && (
                                                    <span className="text-[9px] px-1 rounded bg-[#00E0FF]/20 text-[#00E0FF] font-bold">{p.played_back.count}</span>
                                                )}
                                            </span>
                                            <span className="text-[#00E0FF] font-mono">+{(p.played_back?.total || 0).toFixed(2)}</span>
                                        </div>

                                        {/* Sports */}
                                        <div className="border-t border-white/10 pt-2 mt-1">
                                            <p className="text-[10px] font-bold text-[#3B82F6] uppercase mb-1">Sports</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Wagered / Paid</span>
                                                <span className="text-white font-mono text-xs">{(sports.wagered || 0).toFixed(0)} / {(sports.paid_out || 0).toFixed(0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Bets (W/L)</span>
                                                <span className="text-white font-mono text-xs">{sports.count || 0} ({sports.won || 0}W/{sports.lost || 0}L)</span>
                                            </div>
                                        </div>

                                        {/* Lottery */}
                                        <div className="border-t border-white/10 pt-2 mt-1">
                                            <p className="text-[10px] font-bold text-[#F59E0B] uppercase mb-1">Lottery</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Wagered / Paid</span>
                                                <span className="text-white font-mono text-xs">{(lottery.wagered || 0).toFixed(0)} / {(lottery.paid_out || 0).toFixed(0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Tickets (W/L)</span>
                                                <span className="text-white font-mono text-xs">{lottery.count || 0} ({lottery.won || 0}W/{lottery.lost || 0}L)</span>
                                            </div>
                                        </div>

                                        {/* Blackjack (in-house) */}
                                        {((blackjack.wagered || 0) > 0 || (blackjack.count || 0) > 0) && (
                                        <div className="border-t border-white/10 pt-2 mt-1">
                                            <p className="text-[10px] font-bold text-[#FFD700] uppercase mb-1">Blackjack</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Wagered / Paid</span>
                                                <span className="text-white font-mono text-xs">{(blackjack.wagered || 0).toFixed(0)} / {(blackjack.paid_out || 0).toFixed(0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Hands (W/L/P)</span>
                                                <span className="text-white font-mono text-xs">
                                                    {blackjack.count || 0} ({blackjack.won || 0}W/{blackjack.lost || 0}L{(blackjack.push || 0) > 0 ? `/${blackjack.push}P` : ''})
                                                </span>
                                            </div>
                                            {((blackjack.beto?.wagered || 0) > 0) && (
                                                <div className="flex justify-between">
                                                    <span className="text-[#A0AAB2] text-xs">Beto Bonus (W/P)</span>
                                                    <span className="text-[#FFD700] font-mono text-xs">
                                                        {(blackjack.beto.wagered || 0).toFixed(0)} / {(blackjack.beto.paid_out || 0).toFixed(0)}
                                                    </span>
                                                </div>
                                            )}
                                            {((blackjack.mtd?.wagered || 0) > 0) && (
                                                <div className="flex justify-between">
                                                    <span className="text-[#A0AAB2] text-xs">+M Match Dealer (W/P)</span>
                                                    <span className="text-[#00E1FF] font-mono text-xs">
                                                        {(blackjack.mtd.wagered || 0).toFixed(0)} / {(blackjack.mtd.paid_out || 0).toFixed(0)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Net</span>
                                                <span className={`font-mono font-bold text-xs ${(blackjack.profit || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(blackjack.profit || 0) >= 0 ? '+' : ''}{(blackjack.profit || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        )}

                                        {/* Three Card Poker (in-house) */}
                                        {((tcp.wagered || 0) > 0 || (tcp.rounds || 0) > 0) && (
                                        <div className="border-t border-white/10 pt-2 mt-1" data-testid={`tcp-block-${period}`}>
                                            <p className="text-[10px] font-bold text-[#00E1FF] uppercase mb-1">3-Card Poker</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Wagered / Paid</span>
                                                <span className="text-white font-mono text-xs">{(tcp.wagered || 0).toFixed(0)} / {(tcp.paid_out || 0).toFixed(0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Rounds (W/L/P)</span>
                                                <span className="text-white font-mono text-xs">
                                                    {tcp.rounds || 0} ({tcp.won || 0}W/{tcp.lost || 0}L{(tcp.push || 0) > 0 ? `/${tcp.push}P` : ''})
                                                </span>
                                            </div>
                                            {((tcp.folds || 0) > 0) && (
                                                <div className="flex justify-between">
                                                    <span className="text-[#A0AAB2] text-xs">Folds</span>
                                                    <span className="text-[#FF3B30] font-mono text-xs">{tcp.folds || 0}</span>
                                                </div>
                                            )}
                                            {((tcp.pair_plus?.wagered || 0) > 0) && (
                                                <div className="flex justify-between">
                                                    <span className="text-[#A0AAB2] text-xs">Pair Plus wagered</span>
                                                    <span className="text-[#FF007F] font-mono text-xs">
                                                        {(tcp.pair_plus.wagered || 0).toFixed(0)}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Net</span>
                                                <span className={`font-mono font-bold text-xs ${(tcp.profit || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(tcp.profit || 0) >= 0 ? '+' : ''}{(tcp.profit || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        )}

                                        {/* Craps (in-house) */}
                                        {((craps.wagered || 0) > 0 || (craps.rolls || 0) > 0) && (
                                        <div className="border-t border-white/10 pt-2 mt-1" data-testid={`craps-block-${period}`}>
                                            <p className="text-[10px] font-bold text-[#39FF14] uppercase mb-1">Craps</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Wagered / Paid</span>
                                                <span className="text-white font-mono text-xs">{(craps.wagered || 0).toFixed(0)} / {(craps.paid_out || 0).toFixed(0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Sessions · Rolls</span>
                                                <span className="text-white font-mono text-xs">{craps.sessions || 0} · {craps.rolls || 0}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Net</span>
                                                <span className={`font-mono font-bold text-xs ${(craps.profit || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(craps.profit || 0) >= 0 ? '+' : ''}{(craps.profit || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        )}

                                        {/* Slots (MajikBonus) */}
                                        {((casino.topup_total || 0) > 0 || (casino.redeem_total || 0) > 0) && (
                                        <div className="border-t border-white/10 pt-2 mt-1">
                                            <p className="text-[10px] font-bold text-[#39FF14] uppercase mb-1">Slots</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Topups / Redeems</span>
                                                <span className="text-white font-mono text-xs">
                                                    {(casino.topup_total || 0).toFixed(0)} / {(casino.redeem_total || 0).toFixed(0)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Transactions (T/R)</span>
                                                <span className="text-white font-mono text-xs">
                                                    {(casino.topup_count || 0) + (casino.redeem_count || 0)} ({casino.topup_count || 0}T/{casino.redeem_count || 0}R)
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Net Gross</span>
                                                <span className={`font-mono font-bold text-xs ${(casino.gross || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(casino.gross || 0) >= 0 ? '+' : ''}{(casino.gross || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                        )}

                                        {/* Slots */}
                                        {((slots.wagered || 0) > 0 || (slots.count || 0) > 0) && (
                                        <div className="border-t border-white/10 pt-2 mt-1">
                                            <p className="text-[10px] font-bold text-[#FF007F] uppercase mb-1">Slots (legacy)</p>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Wagered / Paid</span>
                                                <span className="text-white font-mono text-xs">{(slots.wagered || 0).toFixed(0)} / {(slots.paid_out || 0).toFixed(0)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Spins (W/L)</span>
                                                <span className="text-white font-mono text-xs">{slots.count || 0} ({slots.won || 0}W/{slots.lost || 0}L)</span>
                                            </div>
                                            {(slots.jackpot_paid || 0) > 0 && (
                                                <div className="flex justify-between">
                                                    <span className="text-[#A0AAB2] text-xs">Jackpots Paid</span>
                                                    <span className="text-[#FF007F] font-mono text-xs font-bold">
                                                        -{(slots.jackpot_paid || 0).toFixed(0)}
                                                        {(() => {
                                                            const f = slots.jackpot_fires || {};
                                                            const parts = [];
                                                            if (f.blake) parts.push(`${f.blake}B`);
                                                            if (f.grand) parts.push(`${f.grand}G`);
                                                            if (f.super) parts.push(`${f.super}S`);
                                                            return parts.length ? <span className="text-[9px] text-[#A0AAB2] ml-1">({parts.join(' ')})</span> : null;
                                                        })()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                        )}

                                        {/* Combined */}
                                        <div className="border-t border-white/10 pt-2 mt-1">
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Bets Wagered</span>
                                                <span className="text-white font-mono font-bold text-xs">{(combined.total_wagered || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-[#A0AAB2] text-xs">Bets Paid Out</span>
                                                <span className="text-[#FF3B30] font-mono font-bold text-xs">-{(combined.total_paid || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between mt-1 pt-1 border-t border-[#FFD700]/20">
                                                <div className="flex flex-col">
                                                    <span className="text-[#FFD700] font-black" data-testid="report-net-after-free-play-label">NET CASH FLOW AFTER FREE PLAY (NOT PROFIT)</span>
                                                    <span className="text-[9px] text-[#A0AAB2] font-normal -mt-0.5">loaded − redeemed − free play</span>
                                                </div>
                                                <span className={`font-mono font-black text-base ${(combined.cash_profit ?? combined.profit ?? 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(combined.cash_profit ?? combined.profit ?? 0) >= 0 ? '+' : ''}{(combined.cash_profit ?? combined.profit ?? 0).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between mt-1">
                                                <div className="flex flex-col">
                                                    <span className="text-[#A0AAB2] text-[10px]">Bet Margin</span>
                                                    <span className="text-[9px] text-[#666] font-normal -mt-0.5">internal credit margin</span>
                                                </div>
                                                <span className={`font-mono text-xs ${(combined.bet_margin || 0) >= 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                                    {(combined.bet_margin || 0) >= 0 ? '+' : ''}{(combined.bet_margin || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Daily Snapshots history */}
                    <CollapsibleSection title="Daily snapshots history" testid="reports-collapse-snapshots">
                        <DailySnapshotsPanel />
                    </CollapsibleSection>

                    {/* Bet History */}
                    <CollapsibleSection title={`All bets (${allBetsReport.length})`} testid="reports-collapse-all-bets">
                    <div className="card-dark p-6">
                        <h3 className="text-lg font-bold text-white mb-4">All Bets ({allBetsReport.length})</h3>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10">
                                        <TableHead className="text-[#A0AAB2]">Customer</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Bet</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Game</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Wager</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Payout</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Status</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Placed</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {allBetsReport.map((b, i) => (
                                        <TableRow key={i} className="border-white/5">
                                            <TableCell>
                                                <p className="text-white text-sm">
                                                    <PlayerNameLink id={b.user_id} name={b.user_name} testId={`bets-name-${i}`} />
                                                </p>
                                                <p className="text-[#A0AAB2] text-[10px]">{b.user_email}</p>
                                            </TableCell>
                                            <TableCell>
                                                <p className="text-white text-sm">{b.outcome_name}</p>
                                                <p className="text-[#A0AAB2] text-[10px]">{b.market} {b.odds > 0 ? '+' : ''}{b.odds}</p>
                                            </TableCell>
                                            <TableCell>
                                                <p className="text-white text-xs">{b.away_team} @ {b.home_team}</p>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-white">{b.amount?.toFixed(2)}</TableCell>
                                            <TableCell className="text-right font-mono text-[#39FF14]">{b.potential_payout?.toFixed(2)}</TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    b.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' :
                                                    b.status === 'lost' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                                                    b.status === 'pending' ? 'bg-[#FFD700]/20 text-[#FFD700]' :
                                                    b.status === 'cashed_out' ? 'bg-[#00BFFF]/20 text-[#00BFFF]' :
                                                    'bg-white/10 text-[#A0AAB2]'
                                                }`}>{b.status?.toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="text-xs text-[#A0AAB2]">
                                                {b.placed_at ? new Date(b.placed_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                    </CollapsibleSection>

                    {/* Lottery Tickets */}
                    <CollapsibleSection title={`Lottery tickets (${lotteryTickets.length})`} testid="reports-collapse-lottery">
                    <div className="card-dark p-6">
                        <h3 className="text-lg font-bold text-white mb-4">Lottery Tickets ({lotteryTickets.length})</h3>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-white/10">
                                        <TableHead className="text-[#A0AAB2]">Customer</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Numbers</TableHead>
                                        <TableHead className="text-[#A0AAB2]">State / Type</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Wager</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Status</TableHead>
                                        <TableHead className="text-[#A0AAB2] text-right">Payout</TableHead>
                                        <TableHead className="text-[#A0AAB2]">Date</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {lotteryTickets.map((t, i) => {
                                        // Combo / All-Triples rows are grouped server-side and may have
                                        // `numbers` set to the bulk-origin string ("all_triples" / "all_combos_bulk")
                                        // instead of the per-permutation digit array. Render a badge for those.
                                        const numbersArr = Array.isArray(t.numbers) ? t.numbers : null;
                                        const bulkLabel = !numbersArr
                                            ? (t.bet_type === 'trips' ? 'ALL TRIPLES' : t.bet_type === 'combo_all' ? 'ALL COMBOS' : String(t.numbers ?? ''))
                                            : null;
                                        const typeLabel =
                                            t.bet_type === 'straight_box' ? 'Str+Box' :
                                            t.bet_type === 'combo' ? `Combo (${t.ways || ''}-way)` :
                                            t.bet_type === 'trips' ? 'All Triples' :
                                            t.bet_type === 'combo_all' ? 'All Combos' :
                                            t.bet_type;
                                        const wagerDisplay = t.is_combo && t.total_cost != null
                                            ? Number(t.total_cost).toFixed(2)
                                            : (Number(t.amount ?? 0)).toFixed(2);
                                        const payoutNum = Number(t.payout ?? 0);
                                        return (
                                        <TableRow key={t.id || i} className="border-white/5">
                                            <TableCell>
                                                <p className="text-white text-sm">
                                                    <PlayerNameLink id={t.user_id} name={t.user_name} testId={`lottery-name-${i}`} />
                                                </p>
                                                <p className="text-[10px] text-[#A0AAB2]">{t.user_email}</p>
                                            </TableCell>
                                            <TableCell>
                                                {numbersArr ? (
                                                    <div className="flex gap-0.5">
                                                        {numbersArr.map((n, j) => (
                                                            <span key={j} className="w-6 h-6 rounded bg-[#FFD700]/10 text-[#FFD700] text-xs font-bold flex items-center justify-center">{n}</span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="inline-block px-2 py-0.5 rounded bg-[#FF6B35]/15 text-[#FF6B35] text-[10px] font-black uppercase tracking-widest">
                                                        {bulkLabel}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-xs text-white">{t.state} Pick {t.pick_type}</span>
                                                <div className="flex items-center gap-1 mt-0.5">
                                                    <span className="text-[10px] text-[#A0AAB2]">{typeLabel}</span>
                                                    <span
                                                        className={`text-[9px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider ${
                                                            (t.drawing_time || '').toLowerCase() === 'evening'
                                                                ? 'bg-[#1E3A8A]/40 text-[#93C5FD] border border-[#3B82F6]/40'
                                                                : 'bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30'
                                                        }`}
                                                    >
                                                        {(t.drawing_time || '').toLowerCase() === 'evening' ? 'Evening' : 'Midday'}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-white">
                                                {wagerDisplay}
                                                {t.is_combo && t.amount_per_way != null && (
                                                    <span className="block text-[9px] text-[#666]">${Number(t.amount_per_way).toFixed(2)}/way</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                                    t.status === 'won' ? 'bg-[#39FF14]/20 text-[#39FF14]' :
                                                    t.status === 'lost' ? 'bg-[#FF3B30]/20 text-[#FF3B30]' :
                                                    'bg-[#FFD700]/20 text-[#FFD700]'
                                                }`}>{t.status?.toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-[#39FF14]">{payoutNum > 0 ? `+${payoutNum.toFixed(2)}` : '-'}</TableCell>
                                            <TableCell className="text-xs text-[#A0AAB2]">{t.draw_date}</TableCell>
                                        </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                    </CollapsibleSection>
                </>
            )}
        </div>
    );
};
